import type { DemoState } from '../demo-types'

export interface CoachMark {
  id: string
  target: string // data-coach attribute value to find the element
  /** i18n key for the message (resolved via useTranslations('demo')) */
  messageKey: string
  side: 'top' | 'bottom' | 'left' | 'right'
  view: 'audience' | 'artist' | 'both'
  triggerAfter: 'go_live' | 'first_song' | 'immediate'
  prerequisite?: string // ID of another coach mark that must be dismissed first
  /** i18n key for the action button label (resolved via useTranslations('demo')) */
  actionLabelKey?: string
  /** Auto-dismiss this mark when this condition becomes true */
  satisfiedWhen?: (state: DemoState) => boolean
  /** Auto-dismiss when the user clicks the target element */
  dismissOnTargetClick?: boolean
  /** Only show this mark when this condition is true (e.g. feature is enabled) */
  enabledWhen?: (state: DemoState) => boolean
  /** If set, the action button navigates to this URL instead of advancing */
  linkTo?: string
}

export const COACH_MARKS: CoachMark[] = [
  // ─── Pre-event intro marks (immediate, sequenced) ──────────────────
  {
    id: 'intro-audience',
    target: 'audience-view-label',
    messageKey: 'coachIntroAudience',
    actionLabelKey: 'coachGotIt',
    side: 'bottom',
    view: 'audience',
    triggerAfter: 'immediate',
  },
  {
    id: 'intro-artist',
    target: 'artist-view-label',
    messageKey: 'coachIntroArtist',
    actionLabelKey: 'coachCool',
    side: 'bottom',
    view: 'artist',
    triggerAfter: 'immediate',
    prerequisite: 'intro-audience',
  },
  {
    id: 'intro-go-live',
    target: 'go-live-button',
    messageKey: 'coachGoLive',
    actionLabelKey: 'coachLetsGo',
    side: 'top',
    view: 'artist',
    triggerAfter: 'immediate',
    prerequisite: 'intro-artist',
    satisfiedWhen: (s) => s.phase === 'live',
  },

  // ─── First post-go-live: play a song (always first) ───────────────
  {
    id: 'pick-song',
    target: 'pick-song',
    messageKey: 'coachPickSong',
    actionLabelKey: 'coachOnIt',
    side: 'bottom',
    view: 'artist',
    triggerAfter: 'go_live',
    dismissOnTargetClick: true,
    satisfiedWhen: (s) => s.nowPlaying !== null,
  },

  // ─── After first song: show audience now-playing ───────────────────
  {
    id: 'audience-now-playing',
    target: 'audience-now-playing',
    messageKey: 'coachNowPlaying',
    actionLabelKey: 'coachCool',
    side: 'bottom',
    view: 'audience',
    triggerAfter: 'first_song',
    satisfiedWhen: (s) => s.nowPlaying === null, // dismiss if song ends
  },

  // ─── Social links coach ────────────────────────────────────────────
  {
    id: 'social-links',
    target: 'social-links',
    messageKey: 'coachSocialLinks',
    actionLabelKey: 'coachGreat',
    side: 'bottom',
    view: 'audience',
    triggerAfter: 'first_song',
    prerequisite: 'audience-now-playing',
  },

  // ─── Post-first-song intro for audience ───────────────────────────
  {
    id: 'intro-live-audience',
    target: 'request-song',
    messageKey: 'coachLiveAudience',
    actionLabelKey: 'coachAwesome',
    side: 'top',
    view: 'audience',
    triggerAfter: 'first_song',
    prerequisite: 'social-links',
  },

  // ─── More artist marks ────────────────────────────────────────────
  {
    id: 'play-queue',
    target: 'play-queue',
    messageKey: 'coachPlayQueue',
    actionLabelKey: 'coachNice',
    side: 'top',
    view: 'artist',
    triggerAfter: 'go_live',
    prerequisite: 'pick-song',
    enabledWhen: (s) => s.settings.requestsEnabled,
    dismissOnTargetClick: true,
    satisfiedWhen: (s) => s.playedSongs.length > 1,
  },
  {
    id: 'adjust-settings',
    target: 'adjust-settings',
    messageKey: 'coachAdjustSettings',
    actionLabelKey: 'coachGoodToKnow',
    side: 'bottom',
    view: 'artist',
    triggerAfter: 'go_live',
    dismissOnTargetClick: true,
  },

  // ─── Audience marks (after first song) ────────────────────────────
  {
    id: 'request-song',
    target: 'request-song',
    messageKey: 'coachRequestSong',
    actionLabelKey: 'coachTryIt',
    side: 'top',
    view: 'audience',
    triggerAfter: 'first_song',
    enabledWhen: (s) => s.settings.requestsEnabled,
    dismissOnTargetClick: true,
    satisfiedWhen: (s) => s.visitorRequestCount > 0,
  },
  {
    id: 'send-tip',
    target: 'send-tip',
    messageKey: 'coachSendTip',
    actionLabelKey: 'coachSure',
    side: 'top',
    view: 'audience',
    triggerAfter: 'first_song',
    enabledWhen: (s) => s.settings.tipsEnabled,
    dismissOnTargetClick: true,
    satisfiedWhen: (s) => s.visitorHasTipped,
  },
  // ─── Cross-view follow-ups ────────────────────────────────────────
  {
    id: 'artist-sees-requests',
    target: 'play-queue',
    messageKey: 'coachArtistSeesRequests',
    actionLabelKey: 'coachNice',
    side: 'top',
    view: 'artist',
    triggerAfter: 'go_live',
    enabledWhen: (s) => s.settings.requestsEnabled && s.visitorRequestCount > 0 && s.queue.length > 0,
  },
  {
    id: 'artist-sees-tips',
    target: 'artist-tips-stat',
    messageKey: 'coachArtistSeesTips',
    actionLabelKey: 'coachLoveIt',
    side: 'bottom',
    view: 'artist',
    triggerAfter: 'go_live',
    enabledWhen: (s) => s.settings.tipsEnabled && s.visitorHasTipped,
  },
  {
    id: 'send-reaction',
    target: 'send-reaction',
    messageKey: 'coachSendReaction',
    actionLabelKey: 'coachFun',
    side: 'top',
    view: 'audience',
    triggerAfter: 'first_song',
    enabledWhen: (s) => s.settings.reactionsEnabled,
    satisfiedWhen: (s) => s.visitorReactionTokens < 3,
  },
  {
    id: 'send-feedback',
    target: 'send-feedback',
    messageKey: 'coachShareFeedback',
    side: 'top',
    view: 'audience',
    triggerAfter: 'first_song',
    enabledWhen: (s) => s.settings.feedbackEnabled,
    satisfiedWhen: (s) => s.recentFeedback.length > 0,
  },

  // ─── Final summary (shows after all others are exhausted) ─────────
  {
    id: 'demo-summary',
    target: '_center',
    messageKey: 'coachDemoSummary',
    actionLabelKey: 'coachGetStarted',
    side: 'bottom',
    view: 'artist',
    triggerAfter: 'first_song',
    linkTo: '/signup',
  },
]
